import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Reveal, { MaskedLine } from "../components/Reveal";

const CONTACT_ROWS = [
  { icon: MapPin, label: "Works", value: "G.T. Road, Greater Noida, Distt. Gautam Buddha Nagar (U.P.) – 201306" },
  { icon: MapPin, label: "Head Office", value: "KH-266, Kavi Nagar, Ghaziabad (U.P.) – 201002" },
  { icon: Phone, label: "Phone / WhatsApp", value: "+91 98110 92858 · +91 79823 38552" },
  { icon: Mail, label: "Email", value: "info@snpplastech.com" },
  { icon: User, label: "Contact Person", value: "Madhav Agarwal — Business Development Manager" },
  { icon: Clock, label: "Hours", value: "Mon–Sat · 9:00–18:00 IST" },
];

const inputClass =
  "w-full bg-transparent border-0 border-b border-line focus:border-emerald outline-none py-4 placeholder:text-obsidian/35 transition-colors duration-300";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product: searchParams.get("product") || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const web3Key = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
      if (web3Key) {
        const res = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            access_key: web3Key,
            subject: `New website enquiry — ${form.product || "General"}`,
            from_name: "SNP Plastech Website",
            botcheck: "",
            ...form,
          }),
        });
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.message || "Submission failed");
      } else {
        const res = await fetch("https://formsubmit.co/ajax/info@snpplastech.com", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            _subject: `New website enquiry — ${form.product || "General"}`,
            _replyto: form.email,
            _template: "table",
            _honey: "",
            ...form,
          }),
        });
        const result = await res.json();
        const ok = String(result.success) === "true" || result.success === true;
        if (!res.ok || !ok) {
          if ((result.message || "").includes("Activation")) {
            throw new Error("pending-activation");
          }
          throw new Error(result.message || "Submission failed");
        }
      }
      toast.success("Enquiry received — our team will get back within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", product: "", message: "" });
    } catch (err) {
      if (err.message === "pending-activation") {
        toast.info("Enquiry noted — our team will contact you. You can also email us directly at info@snpplastech.com.");
        setForm({ name: "", email: "", phone: "", company: "", product: "", message: "" });
      } else {
        toast.error("Could not send your enquiry. Please try again or email us at info@snpplastech.com.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 lg:pt-40 pb-24 lg:pb-36 max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-6">Contact</p>
          <h1 className="font-display font-semibold tracking-tighter leading-[0.95] text-6xl sm:text-7xl">
            <MaskedLine>START AN</MaskedLine>
            <MaskedLine delay={0.12}>ENQUIRY<span className="text-emerald">.</span></MaskedLine>
          </h1>
          <Reveal delay={0.2}>
            <p className="text-obsidian/60 max-w-md mt-8 leading-relaxed">
              Tell us about your product, volumes and timelines. Share drawings or samples — we reverse-engineer too.
            </p>
          </Reveal>
          <div className="mt-12 space-y-0">
            {CONTACT_ROWS.map((row) => (
              <Reveal key={row.label}>
                <div className="flex gap-5 py-5 border-b border-line" data-testid={`contact-info-${row.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <row.icon className="w-5 h-5 text-emerald shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-obsidian/40">{row.label}</p>
                    <p className="mt-1.5 text-sm leading-relaxed">{row.value}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={handleSubmit} data-testid="enquiry-form" className="border border-line bg-white p-8 lg:p-12">
            <p className="font-display font-medium text-2xl tracking-tight mb-8">Tell us what you need</p>
            <div className="grid sm:grid-cols-2 gap-x-8">
              <div>
                <label htmlFor="enquiry-name" className="text-xs uppercase tracking-[0.2em] font-semibold text-obsidian/40">Name *</label>
                <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputClass} />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="text-xs uppercase tracking-[0.2em] font-semibold text-obsidian/40">Email *</label>
                <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputClass} />
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="text-xs uppercase tracking-[0.2em] font-semibold text-obsidian/40">Phone</label>
                <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 …" className={inputClass} />
              </div>
              <div>
                <label htmlFor="enquiry-company" className="text-xs uppercase tracking-[0.2em] font-semibold text-obsidian/40">Company</label>
                <input id="enquiry-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className={inputClass} />
              </div>
            </div>
            <div className="mt-2">
              <label htmlFor="enquiry-product" className="text-xs uppercase tracking-[0.2em] font-semibold text-obsidian/40">Product of interest</label>
              <input id="enquiry-product" data-testid="enquiry-product-input" value={form.product} onChange={set("product")} placeholder="e.g. PET Pump & Spray Bottles" className={inputClass} />
            </div>
            <div className="mt-2">
              <label htmlFor="enquiry-message" className="text-xs uppercase tracking-[0.2em] font-semibold text-obsidian/40">Message *</label>
              <textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Quantities, sizes, timelines, decoration needs…" className={`${inputClass} resize-none`} />
            </div>
            <button
              type="submit"
              data-testid="enquiry-submit-button"
              disabled={submitting}
              className="group mt-10 w-full inline-flex items-center justify-center gap-3 bg-obsidian text-bone font-medium px-8 py-4 rounded-full hover:bg-emerald transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Send Enquiry"}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
            </button>
          </form>
        </Reveal>
      </div>

      <Reveal className="mt-20 lg:mt-28">
        <div data-testid="contact-map" className="border border-line overflow-hidden">
          <iframe
            title="SNP Plastech — G.T. Road, Greater Noida"
            src="https://www.google.com/maps?q=SNP%20Plastech%2C%20G.T.%20Road%2C%20Greater%20Noida%2C%20Uttar%20Pradesh&z=15&output=embed"
            className="w-full h-[380px] lg:h-[460px] grayscale-[35%] contrast-[1.05]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-obsidian/50 mt-4">
          Works · G.T. Road, Greater Noida — direct connectivity to Delhi, Noida &amp; the Eastern Peripheral Expressway
        </p>
      </Reveal>
    </div>
  );
};

export default Contact;
